import React, { useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Stack, Group, Text, Badge, Button, Tooltip, Collapse, Paper, Progress, Divider, TextInput, Textarea, ActionIcon, Modal, Select, Alert } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconEdit, IconClipboardCheck, IconSparkles, IconTrash, IconPlus, IconDeviceFloppy, IconAlertCircle, IconX, IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { updateRiskName, discardSavedRisk, addManualRisk } from '../redux/slices/risksSlice';
import { saveResidualRisk } from '../redux/slices/residualRiskSlice';
import RiskMitigationsInlineV2 from './components/RiskMitigationsInlineV2';
import { authFetch } from '../utils/authFetch';

function toTitleCase(str = '') {
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w|\s\w/g, (m) => m.toUpperCase());
}

// Use RiskLandscape color scheme for consistency
const priorityColors = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Red
  Significant: '#B94D4D', // Amber
  Moderate: '#D4AA7D',    // Light Orange
  Low: '#B8C2B7',         // Light Grey-Blue
  Negligible: '#D8D8D8',  // Light Grey
  None: '#EBEBEB'         // Very Light Grey
};

// Helper function to get priority color with proper capitalization
const getPriorityColor = (priority) => {
  if (!priority) return '#EBEBEB';
  // Capitalize first letter to match priorityColors keys
  const normalized = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  return priorityColors[normalized] || '#EBEBEB';
};

export default function SectionAssessmentCardV2({
  modelId,
  assessmentUuid,
  areaName,
  areaData,
  originalAreaData,
  residualRiskData,
  onEvaluateAction,
  onEditCard,
  isExpanded = false,
  onToggleExpand,
  isMinimized = false
}) {
  const dispatch = useDispatch();
  const savedControls = useSelector((s) => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector((s) => s.controls.savedControlEvaluations || {});

  // Debug logging for residual risk data
  console.log(`[SectionAssessmentCardV2] ${areaName} - residualRiskData:`, residualRiskData);
  console.log(`[SectionAssessmentCardV2] ${areaName} - residualRiskData keys:`, Object.keys(residualRiskData));

  // State for inline risk editing
  const [editingRiskId, setEditingRiskId] = useState(null);
  const [editedRiskName, setEditedRiskName] = useState('');

  // State for priority editing
  const [editingPriorityRiskId, setEditingPriorityRiskId] = useState(null);
  const [editedPriority, setEditedPriority] = useState('');

  // State for AI refinement modal
  const [refineModalOpened, setRefineModalOpened] = useState(false);
  const [refineRiskData, setRefineRiskData] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState('specific');
  const [customInstruction, setCustomInstruction] = useState('');
  const [refinedSuggestion, setRefinedSuggestion] = useState(null);
  const [isRefining, setIsRefining] = useState(false);

  // State for manual risk addition
  const [showAddRisk, setShowAddRisk] = useState(null);
  const [newRiskName, setNewRiskName] = useState('');

  // State for residual risk editing
  const [editingResidualRiskId, setEditingResidualRiskId] = useState(null);
  const [editingActionPlanRiskId, setEditingActionPlanRiskId] = useState(null);

  // compute changed
  const isChanged = useMemo(() => {
    if (!originalAreaData || !areaData) return false;
    try {
      const orig = JSON.stringify({ riskSources: originalAreaData.riskSources });
      const curr = JSON.stringify({ riskSources: areaData.riskSources });
      return orig !== curr;
    } catch {
      return false;
    }
  }, [originalAreaData, areaData]);

  // Enhanced stats calculation
  const areaStats = useMemo(() => {
    const priorityCounts = {};
    let residualCount = 0;
    let totalActions = 0;
    let evaluatedActions = 0;
    let completedActions = 0;

    Object.values(areaData?.areaRisks || {}).forEach((risksArr) => {
      (Array.isArray(risksArr) ? risksArr : [])
        .filter(r => r.status !== 'Discarded') // Filter out discarded risks
        .forEach((r) => {
          // Normalize priority to proper case (e.g., "moderate" -> "Moderate")
          const rawPr = (r.priority || 'None').toString();
          const pr = rawPr.charAt(0).toUpperCase() + rawPr.slice(1).toLowerCase();
          priorityCounts[pr] = (priorityCounts[pr] || 0) + 1;
          if (residualRiskData[r.riskId]) residualCount += 1;

          // Count actions for this risk
          const actions = savedControls[r.riskId] || [];
          totalActions += actions.length;

          actions.forEach(action => {
            const evalData = savedControlEvaluations[r.riskId]?.[action.controlId]?.[0] || {};
            if (evalData.control_recurrence || evalData.executed_next || evalData.self_evaluation) {
              evaluatedActions += 1;
              if (evalData.control_recurrence === 'Complete' || evalData.executed_next === 'Complete') {
                completedActions += 1;
              }
            }
          });
        });
    });

    const topPriorities = Object.entries(priorityCounts)
      .sort((a, b) => {
        const priorityOrder = { Critical: 5, High: 4, Significant: 3, Moderate: 2, Low: 1, Negligible: 0, None: 0 };
        return (priorityOrder[b[0]] || 0) - (priorityOrder[a[0]] || 0);
      })
      .slice(0, 3);

    const totalRisks = Object.values(priorityCounts).reduce((sum, count) => sum + count, 0);
    const progressPercentage = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;

    return {
      priorityCounts,
      topPriorities,
      residualCount,
      totalRisks,
      totalActions,
      evaluatedActions,
      completedActions,
      progressPercentage,
    };
  }, [areaData, residualRiskData, savedControls, savedControlEvaluations]);

  // flattened risks list for expanded view
  const risksByCategory = useMemo(() => {
    const map = new Map();
    Object.entries(areaData?.areaRisks || {}).forEach(([category, arr]) => {
      if (Array.isArray(arr) && arr.length > 0) {
        // Filter out discarded risks
        const activeRisks = arr.filter(risk => risk.status !== 'Discarded');
        if (activeRisks.length > 0) {
          map.set(category, activeRisks);
        }
      }
    });
    return map;
  }, [areaData]);

  const handleExpand = () => {
    onToggleExpand(areaName);
  };

  // Inline editing handlers
  const handleStartEdit = (risk) => {
    setEditingRiskId(risk.riskId);
    setEditedRiskName(risk.riskName || '');
  };

  const handleCancelEdit = () => {
    setEditingRiskId(null);
    setEditedRiskName('');
  };

  // Priority editing handlers
  const handleStartPriorityEdit = (risk) => {
    setEditingPriorityRiskId(risk.riskId);
    setEditedPriority(risk.priority || '');
  };

  const handleCancelPriorityEdit = () => {
    setEditingPriorityRiskId(null);
    setEditedPriority('');
  };

  // Show rationale modal
  const handleShowRationale = (risk) => {
    modals.open({
      title: 'Priority Rationale',
      children: (
        <Stack gap="md">
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>{risk.riskName}</Text>
                <Badge
                  size="sm"
                  variant="filled"
                  style={{
                    backgroundColor: getPriorityColor(risk.priority),
                    color: risk.priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A'
                  }}
                >
                  {risk.priority}
                </Badge>
              </Group>
            </Stack>
          </Paper>
          <Stack gap="xs">
            <Text size="sm" fw={600} c="blue.7">AI-Generated Rationale:</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {risk.rationale || 'No rationale available for this risk.'}
            </Text>
          </Stack>
        </Stack>
      ),
      size: 'lg'
    });
  };

  const handleShowResidualRiskExplanation = (risk) => {
    const residualData = residualRiskData[risk.riskId];
    modals.open({
      title: 'Residual Risk Explanation',
      children: (
        <Stack gap="md">
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>{risk.riskName}</Text>
                <Badge
                  variant="filled"
                  size="sm"
                  style={{
                    backgroundColor: getPriorityColor(residualData?.residual_rating),
                    color: residualData?.residual_rating?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A'
                  }}
                >
                  Residual: {residualData?.residual_rating || 'N/A'}
                </Badge>
              </Group>
            </Stack>
          </Paper>
          <Stack gap="xs">
            <Text size="sm" fw={600} c="blue.7">AI-Generated Explanation:</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {residualData?.residual_explanation || 'No explanation available for this residual risk.'}
            </Text>
          </Stack>
        </Stack>
      ),
      size: 'lg'
    });
  };

  const handleShowActionPlanAnalysis = (risk) => {
    const residualData = residualRiskData[risk.riskId];
    const actionPlanRating = residualData?.action_plan_rating || residualData?.actionPlanRating;
    const actionPlanAnalysis = residualData?.action_plan_analysis || residualData?.actionPlanAnalysis;

    modals.open({
      title: 'Action Plan Analysis',
      children: (
        <Stack gap="md">
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>{risk.riskName}</Text>
                <Badge
                  variant="light"
                  size="sm"
                  color={
                    actionPlanRating === 'Excellent' ? 'green' :
                    actionPlanRating === 'Very Good' ? 'teal' :
                    actionPlanRating === 'Good' ? 'blue' :
                    actionPlanRating === 'Fair' ? 'yellow' :
                    actionPlanRating === 'Poor' ? 'orange' : 'red'
                  }
                >
                  Plan: {actionPlanRating || 'N/A'}
                </Badge>
              </Group>
            </Stack>
          </Paper>
          <Stack gap="xs">
            <Text size="sm" fw={600} c="blue.7">AI-Generated Analysis:</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {actionPlanAnalysis || 'No analysis available for this action plan.'}
            </Text>
          </Stack>
        </Stack>
      ),
      size: 'lg'
    });
  };

  const handleSavePriority = async (risk) => {
    if (!editedPriority) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a priority level',
        color: 'red'
      });
      return;
    }

    try {
      // Update Redux state
      await dispatch({
        type: 'SET_RISK_PRIORITY_SUCCESS',
        payload: {
          modelId,
          sectionName: areaName,
          category: risk.category,
          riskId: risk.riskId,
          priority: editedPriority,
          rationale: risk.rationale // Preserve existing rationale
        }
      });

      // Persist to database
      const response = await authFetch('/.netlify/functions/saveRiskPriority', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid: modelId,
          assessmentUuid,
          riskId: risk.riskId,
          priority: editedPriority,
          rationale: risk.rationale || '' // Preserve rationale
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save priority to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Priority updated successfully',
        color: 'green'
      });

      handleCancelPriorityEdit();
    } catch (error) {
      console.error('Failed to update priority:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update priority',
        color: 'red'
      });
    }
  };

  const handleSaveEdit = async (risk) => {
    if (!editedRiskName.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Risk name cannot be empty',
        color: 'red'
      });
      return;
    }

    try {
      // Update Redux state first
      dispatch(updateRiskName({
        assessmentUuid,
        sectionName: areaName,
        originalName: risk.riskName,
        newName: editedRiskName.trim(),
      }));

      // Persist to database
      const response = await authFetch('/.netlify/functions/updateRiskScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riskId: risk.riskId,
          riskName: editedRiskName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Risk updated successfully',
        color: 'green'
      });

      handleCancelEdit();
    } catch (error) {
      console.error('Failed to update risk:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update risk',
        color: 'red'
      });
    }
  };

  // AI refinement handlers
  const handleRefineRisk = (risk) => {
    setRefineRiskData(risk);
    setRefineModalOpened(true);
    setRefinedSuggestion(null);
    setRefineInstruction('specific');
    setCustomInstruction('');
  };

  const handleGenerateRefinement = async () => {
    setIsRefining(true);

    let userInstruction = '';
    if (refineInstruction === 'specific') {
      userInstruction = 'Make the risk more specific and focused';
    } else if (refineInstruction === 'technical') {
      userInstruction = 'Make the risk more technical in wording';
    } else if (refineInstruction === 'formal') {
      userInstruction = 'Make the risk more formal in tone';
    } else if (refineInstruction === 'custom') {
      userInstruction = customInstruction;
    }

    try {
      // Call backend directly instead of Redux action
      const response = await authFetch('/.netlify/functions/refineRiskLLM', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalRiskText: refineRiskData.riskName,
          userInstruction,
          context: {}
        })
      });

      if (!response.ok) {
        throw new Error('Refinement request failed');
      }

      const { refinedRisk } = await response.json();
      setRefinedSuggestion(refinedRisk);

    } catch (error) {
      console.error('Refinement failed:', error);
      notifications.show({
        title: 'Refinement Failed',
        message: 'Could not generate refinement. Please try again.',
        color: 'red'
      });
    } finally {
      setIsRefining(false);
    }
  };

  const handleApplyRefinement = async () => {
    if (!refinedSuggestion) return;

    try {
      // Update Redux state
      dispatch(updateRiskName({
        assessmentUuid,
        sectionName: areaName,
        originalName: refineRiskData.riskName,
        newName: refinedSuggestion,
      }));

      // Persist to database
      const response = await authFetch('/.netlify/functions/updateRiskScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riskId: refineRiskData.riskId,
          riskName: refinedSuggestion
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Refined risk applied',
        color: 'green'
      });

      setRefineModalOpened(false);
      setRefineRiskData(null);
      setRefinedSuggestion(null);
    } catch (error) {
      console.error('Failed to apply refinement:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to apply refinement',
        color: 'red'
      });
    }
  };

  // Delete risk handler
  const handleDeleteRisk = (risk) => {
    console.log('[handleDeleteRisk] Called with risk:', risk);

    modals.openConfirmModal({
      title: 'Delete Risk',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to delete this risk?
          </Text>
          <Paper withBorder p="sm" bg="red.0">
            <Text size="sm" fw={500}>{risk.riskName}</Text>
          </Paper>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            This will also delete:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>All associated mitigation actions</li>
              <li>Action evaluations</li>
              <li>Residual risk assessments</li>
            </ul>
          </Alert>
        </Stack>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        console.log('[handleDeleteRisk] Confirmed, starting deletion...');
        console.log('[handleDeleteRisk] Risk ID:', risk.riskId);
        console.log('[handleDeleteRisk] Model ID:', modelId);
        console.log('[handleDeleteRisk] Area Name:', areaName);

        try {
          // Update Redux state
          console.log('[handleDeleteRisk] Dispatching discardSavedRisk to Redux...');
          dispatch(discardSavedRisk({ assessmentUuid, sectionName: areaName, riskId: risk.riskId }));
          console.log('[handleDeleteRisk] ✓ Redux state updated');

          // Persist to database
          console.log('[handleDeleteRisk] Calling backend deleteRiskScenario...');
          const response = await authFetch('/.netlify/functions/deleteRiskScenario', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              riskId: risk.riskId
            })
          });

          console.log('[handleDeleteRisk] Backend response status:', response.status);
          const responseData = await response.json();
          console.log('[handleDeleteRisk] Backend response data:', responseData);

          if (!response.ok) {
            throw new Error(`Failed to delete from database: ${responseData.error || response.statusText}`);
          }

          console.log('[handleDeleteRisk] ✓ Successfully deleted from database');

          notifications.show({
            title: 'Success',
            message: 'Risk deleted successfully',
            color: 'green'
          });
        } catch (error) {
          console.error('[handleDeleteRisk] ❌ Error during deletion:', error);
          console.error('[handleDeleteRisk] Error stack:', error.stack);
          notifications.show({
            title: 'Error',
            message: `Failed to delete risk: ${error.message}`,
            color: 'red'
          });
        }
      }
    });
  };

  // Add risk handler
  const handleAddRisk = async (category) => {
    if (!newRiskName.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Risk name cannot be empty',
        color: 'red'
      });
      return;
    }

    try {
      // First, persist to database to get riskId
      const response = await authFetch('/.netlify/functions/createRiskScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          riskArea: areaName,
          category,
          riskName: newRiskName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create risk in database');
      }

      await response.json();

      // Update Redux state with the new risk
      // Note: RTK addManualRisk generates its own riskId; the backend risk will sync on reload
      dispatch(addManualRisk({
        assessmentUuid,
        sectionName: areaName,
        category,
        riskName: newRiskName.trim(),
      }));

      notifications.show({
        title: 'Success',
        message: 'Risk added successfully',
        color: 'green'
      });

      setNewRiskName('');
      setShowAddRisk(null);
    } catch (error) {
      console.error('Failed to add risk:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add risk',
        color: 'red'
      });
    }
  };

  // Residual risk rating save handler
  const handleSaveResidualRating = useCallback(async (risk, newValue) => {
    const riskData = residualRiskData[risk.riskId] || {};
    const oldValue = riskData.residual_rating;
    if (newValue === oldValue) {
      setEditingResidualRiskId(null);
      return;
    }

    try {
      await dispatch(saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: risk.riskId,
        riskName: risk.riskName,
        priority: risk.priority,
        residual_rating: newValue,
        residual_explanation: riskData.residual_explanation,
        action_plan_rating: riskData.action_plan_rating || riskData.actionPlanRating,
        action_plan_analysis: riskData.action_plan_analysis || riskData.actionPlanAnalysis,
      })).unwrap();

      notifications.show({
        title: 'Residual Risk Updated',
        message: `Changed from ${oldValue} to ${newValue}`,
        color: 'blue'
      });

      setEditingResidualRiskId(null);
    } catch (error) {
      console.error('[SectionAssessmentCardV2] Error saving residual rating:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update residual risk rating',
        color: 'red'
      });
    }
  }, [dispatch, residualRiskData, modelId, assessmentUuid]);

  // Action plan rating save handler
  const handleSaveActionPlanRating = useCallback(async (risk, newValue) => {
    const riskData = residualRiskData[risk.riskId] || {};
    const oldValue = riskData.action_plan_rating || riskData.actionPlanRating;
    if (newValue === oldValue) {
      setEditingActionPlanRiskId(null);
      return;
    }

    try {
      await dispatch(saveResidualRisk({
        modelId,
        assessmentUuid,
        riskId: risk.riskId,
        riskName: risk.riskName,
        priority: risk.priority,
        residual_rating: riskData.residual_rating,
        residual_explanation: riskData.residual_explanation,
        action_plan_rating: newValue,
        action_plan_analysis: riskData.action_plan_analysis || riskData.actionPlanAnalysis,
      })).unwrap();

      notifications.show({
        title: 'Action Plan Updated',
        message: `Changed from ${oldValue} to ${newValue}`,
        color: 'blue'
      });

      setEditingActionPlanRiskId(null);
    } catch (error) {
      console.error('[SectionAssessmentCardV2] Error saving action plan rating:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update action plan rating',
        color: 'red'
      });
    }
  }, [dispatch, residualRiskData, modelId, assessmentUuid]);

  // Minimized view for when another card is expanded
  if (isMinimized) {
    return (
      <Card
        withBorder
        radius="md"
        p="sm"
        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
        onClick={handleExpand}
        bg="gray.0"
      >
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <IconChevronRight size={16} />
            <Text fw={600} size="sm">{toTitleCase(areaName)}</Text>
            {isChanged && <Badge color="yellow" variant="light" size="xs">Changed</Badge>}
          </Group>
          <Group gap="xs">
            <Badge variant="light" color="indigo" size="xs">
              {areaStats.totalRisks} risks
            </Badge>
            <Badge variant="light" color="teal" size="xs">
              {areaStats.progressPercentage}% done
            </Badge>
          </Group>
        </Group>
      </Card>
    );
  }

  const caret = isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />;

  return (
    <Card 
      withBorder 
      radius="md" 
      p={isExpanded ? "lg" : "md"}
      style={{ 
        transition: 'all 0.3s ease',
        minHeight: isExpanded ? '400px' : 'auto'
      }}
    >
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between" align="flex-start">
          <Group 
            gap="sm" 
            align="center" 
            onClick={handleExpand} 
            style={{ cursor: 'pointer', flex: 1 }}
          >
            {caret}
            <Stack gap={2}>
              <Group gap="xs" align="center">
                <Text fw={700} size={isExpanded ? "lg" : "md"}>
                  {toTitleCase(areaName)}
                </Text>
                {isChanged && <Badge color="yellow" variant="light" size="sm">Modified</Badge>}
              </Group>
              <Text size="xs" c="dimmed">
                {areaStats.totalRisks} risks identified • {areaStats.totalActions} actions planned
              </Text>
            </Stack>
          </Group>

          {/* Priority Counter Badges */}
          <Group gap="xs" align="center">
            {areaStats.topPriorities.map(([priority, count]) => (
              <Tooltip key={priority} label={`${priority}: ${count}`}>
                <Badge
                  variant="filled"
                  size="lg"
                  style={{
                    backgroundColor: getPriorityColor(priority),
                    color: priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A',
                    fontWeight: 700,
                    minWidth: '32px',
                    padding: '8px 10px'
                  }}
                >
                  {count}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Group>

        {/* Progress Section - Always Visible */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>Mitigation Progress</Text>
            <Text size="sm" c="dimmed">{areaStats.progressPercentage}%</Text>
          </Group>
          <Progress
            value={areaStats.progressPercentage}
            color={areaStats.progressPercentage > 75 ? 'green' : areaStats.progressPercentage > 50 ? 'yellow' : 'red'}
            size="sm"
            radius="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {areaStats.completedActions} of {areaStats.totalActions} actions complete
            </Text>
            <Text size="xs" c="dimmed">
              {areaStats.evaluatedActions} evaluated
            </Text>
          </Group>
        </Stack>

        {/* Quick Actions - Collapsed View */}
        {!isExpanded && (
          <>
            <Divider />
            <Group justify="center">
              <Tooltip label="Expand to view risk details">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconEdit size={14} />}
                  onClick={handleExpand}
                >
                  Edit Card
                </Button>
              </Tooltip>
            </Group>
          </>
        )}

        {/* Expanded Content */}
        <Collapse in={isExpanded}>
          <Stack gap="lg">
            <Divider label="Risk Details" labelPosition="left" />
            
            {risksByCategory.size === 0 ? (
              <Card withBorder radius="sm" p="md" bg="gray.0">
                <Text c="dimmed" ta="center">No risks identified in this section yet.</Text>
              </Card>
            ) : (
              <Stack gap="md">
                {[...risksByCategory.entries()].map(([category, risks]) => (
                  <Card key={category} withBorder radius="sm" p="md" bg="blue.0">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="md">{toTitleCase(category)}</Text>
                        <Badge variant="light" color="blue" size="sm">
                          {risks.length} {risks.length === 1 ? 'risk' : 'risks'}
                        </Badge>
                      </Group>

                      <Stack gap="sm">
                        {risks.map((risk) => (
                          <Paper key={risk.riskId} withBorder radius="sm" p="md" bg="white">
                            <Stack gap="sm">
                              {/* Row 1: Risk Name + Action Buttons */}
                              <Group justify="space-between" align="flex-start">
                                <Stack gap={4} style={{ flex: 1 }}>
                                  {editingRiskId === risk.riskId ? (
                                    // EDIT MODE
                                    <Stack gap="xs">
                                      <TextInput
                                        value={editedRiskName}
                                        onChange={(e) => setEditedRiskName(e.target.value)}
                                        placeholder="Risk name"
                                        style={{ flex: 1 }}
                                        size="sm"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveEdit(risk);
                                          if (e.key === 'Escape') handleCancelEdit();
                                        }}
                                      />
                                      <Group gap="xs">
                                        <Button size="xs" onClick={() => handleSaveEdit(risk)} leftSection={<IconDeviceFloppy size={12} />}>
                                          Save
                                        </Button>
                                        <Button size="xs" variant="subtle" onClick={handleCancelEdit}>
                                          Cancel
                                        </Button>
                                      </Group>
                                    </Stack>
                                  ) : (
                                    // VIEW MODE
                                    <Stack gap="xs">
                                      <Text fw={600} size="sm" style={{ wordBreak: 'break-word' }}>
                                        {risk.riskName}
                                      </Text>
                                      {risk.riskDescription && (
                                        <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                                          {risk.riskDescription}
                                        </Text>
                                      )}
                                    </Stack>
                                  )}
                                </Stack>

                                {/* Action Buttons */}
                                {editingRiskId !== risk.riskId && (
                                  <Group gap="xs">
                                    <Tooltip label="Refine with AI">
                                      <ActionIcon size="sm" variant="subtle" color="violet" onClick={() => handleRefineRisk(risk)}>
                                        <IconSparkles size={14} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Edit risk name">
                                      <ActionIcon size="sm" variant="subtle" onClick={() => handleStartEdit(risk)}>
                                        <IconEdit size={14} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Delete risk">
                                      <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        color="red"
                                        onClick={() => {
                                          console.log('[DELETE BUTTON] Clicked! Risk:', risk.riskId);
                                          handleDeleteRisk(risk);
                                        }}
                                      >
                                        <IconTrash size={14} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                )}
                              </Group>

                              {/* Row 2: Assessment Badges with Dividers */}
                              <Group gap="xs" align="center">
                                {/* Priority Badge */}
                                {editingPriorityRiskId === risk.riskId ? (
                                  // PRIORITY EDIT MODE
                                  <Group gap="xs">
                                    <Text size="xs" c="dimmed">Priority:</Text>
                                    <Select
                                      value={editedPriority}
                                      onChange={setEditedPriority}
                                      data={[
                                        { value: 'Critical', label: '🔴 Critical' },
                                        { value: 'High', label: '⚠️ High' },
                                        { value: 'Significant', label: '🟡 Significant' },
                                        { value: 'Moderate', label: '🔵 Moderate' },
                                        { value: 'Low', label: '🟢 Low' },
                                        { value: 'Negligible', label: '⚪ Negligible' }
                                      ]}
                                      size="xs"
                                      w={140}
                                    />
                                    <ActionIcon size="xs" color="green" onClick={() => handleSavePriority(risk)}>
                                      <IconCheck size={12} />
                                    </ActionIcon>
                                    <ActionIcon size="xs" color="gray" onClick={handleCancelPriorityEdit}>
                                      <IconX size={12} />
                                    </ActionIcon>
                                  </Group>
                                ) : (
                                  // PRIORITY VIEW MODE
                                  risk.priority && (
                                    <Group gap={4}>
                                      <Text size="xs" c="dimmed">Priority:</Text>
                                      <Tooltip label="Click to edit priority">
                                        <Badge
                                          size="sm"
                                          variant="filled"
                                          style={{
                                            backgroundColor: getPriorityColor(risk.priority),
                                            color: risk.priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A',
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => handleStartPriorityEdit(risk)}
                                        >
                                          {risk.priority}
                                        </Badge>
                                      </Tooltip>
                                      {risk.rationale && (
                                        <Tooltip label="View priority rationale">
                                          <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="blue"
                                            onClick={() => handleShowRationale(risk)}
                                          >
                                            <IconInfoCircle size={14} />
                                          </ActionIcon>
                                        </Tooltip>
                                      )}
                                    </Group>
                                  )
                                )}

                                {/* Divider after Priority (only if priority exists and residual data exists) */}
                                {risk.priority && residualRiskData[risk.riskId] && (
                                  <Divider orientation="vertical" size="xs" color="gray.3" style={{ height: '20px' }} />
                                )}

                                {/* Residual Risk Badge */}
                                {residualRiskData[risk.riskId] && (
                                  editingResidualRiskId === risk.riskId ? (
                                    <Group gap="xs">
                                      <Text size="xs" c="dimmed">Residual Risk:</Text>
                                      <Select
                                        value={residualRiskData[risk.riskId].residual_rating || ''}
                                        onChange={(value) => handleSaveResidualRating(risk, value)}
                                        data={[
                                          { value: 'Negligible', label: 'Negligible' },
                                          { value: 'Low', label: 'Low' },
                                          { value: 'Moderate', label: 'Moderate' },
                                          { value: 'Significant', label: 'Significant' },
                                          { value: 'High', label: 'High' },
                                          { value: 'Critical', label: 'Critical' }
                                        ]}
                                        size="xs"
                                        w={120}
                                        placeholder="Select..."
                                      />
                                      <ActionIcon size="xs" color="gray" onClick={() => setEditingResidualRiskId(null)}>
                                        <IconX size={12} />
                                      </ActionIcon>
                                    </Group>
                                  ) : (
                                    <Group gap={4}>
                                      <Text size="xs" c="dimmed">Residual Risk:</Text>
                                      <Tooltip label="Click to edit residual risk rating">
                                        <Badge
                                          variant="filled"
                                          size="xs"
                                          style={{
                                            backgroundColor: getPriorityColor(residualRiskData[risk.riskId].residual_rating),
                                            color: residualRiskData[risk.riskId].residual_rating?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A',
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => setEditingResidualRiskId(risk.riskId)}
                                        >
                                          {residualRiskData[risk.riskId].residual_rating || 'N/A'}
                                        </Badge>
                                      </Tooltip>
                                      {residualRiskData[risk.riskId].residual_explanation && (
                                        <Tooltip label="View residual risk explanation">
                                          <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="blue"
                                            onClick={() => handleShowResidualRiskExplanation(risk)}
                                          >
                                            <IconInfoCircle size={14} />
                                          </ActionIcon>
                                        </Tooltip>
                                      )}
                                    </Group>
                                  )
                                )}

                                {/* Divider after Residual (only if residual exists and action plan exists) */}
                                {residualRiskData[risk.riskId] && residualRiskData[risk.riskId].action_plan_rating && (
                                  <Divider orientation="vertical" size="xs" color="gray.3" style={{ height: '20px' }} />
                                )}

                                {/* Action Plan Badge */}
                                {residualRiskData[risk.riskId]?.action_plan_rating && (
                                  editingActionPlanRiskId === risk.riskId ? (
                                    <Group gap="xs">
                                      <Text size="xs" c="dimmed">Mitigation Efficiency:</Text>
                                      <Select
                                        value={residualRiskData[risk.riskId].action_plan_rating || residualRiskData[risk.riskId].actionPlanRating || ''}
                                        onChange={(value) => handleSaveActionPlanRating(risk, value)}
                                        data={[
                                          { value: 'Unsatisfactory', label: 'Unsatisfactory' },
                                          { value: 'Poor', label: 'Poor' },
                                          { value: 'Fair', label: 'Fair' },
                                          { value: 'Good', label: 'Good' },
                                          { value: 'Very Good', label: 'Very Good' },
                                          { value: 'Excellent', label: 'Excellent' }
                                        ]}
                                        size="xs"
                                        w={130}
                                        placeholder="Select..."
                                      />
                                      <ActionIcon size="xs" color="gray" onClick={() => setEditingActionPlanRiskId(null)}>
                                        <IconX size={12} />
                                      </ActionIcon>
                                    </Group>
                                  ) : (
                                    <Group gap={4}>
                                      <Text size="xs" c="dimmed">Mitigation Efficiency:</Text>
                                      <Tooltip label="Click to edit action plan rating">
                                        <Badge
                                          variant="light"
                                          size="xs"
                                          color={
                                            residualRiskData[risk.riskId].action_plan_rating === 'Excellent' ? 'green' :
                                            residualRiskData[risk.riskId].action_plan_rating === 'Very Good' ? 'teal' :
                                            residualRiskData[risk.riskId].action_plan_rating === 'Good' ? 'blue' :
                                            residualRiskData[risk.riskId].action_plan_rating === 'Fair' ? 'yellow' :
                                            residualRiskData[risk.riskId].action_plan_rating === 'Poor' ? 'orange' : 'red'
                                          }
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => setEditingActionPlanRiskId(risk.riskId)}
                                        >
                                          {residualRiskData[risk.riskId].action_plan_rating}
                                        </Badge>
                                      </Tooltip>
                                      {(residualRiskData[risk.riskId].action_plan_analysis || residualRiskData[risk.riskId].actionPlanAnalysis) && (
                                        <Tooltip label="View action plan analysis">
                                          <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="blue"
                                            onClick={() => handleShowActionPlanAnalysis(risk)}
                                          >
                                            <IconInfoCircle size={14} />
                                          </ActionIcon>
                                        </Tooltip>
                                      )}
                                    </Group>
                                  )
                                )}
                              </Group>

                              {/* Risk Mitigations */}
                              <RiskMitigationsInlineV2
                                modelId={modelId}
                                assessmentUuid={assessmentUuid}
                                risk={risk}
                                onEvaluate={() => onEvaluateAction({ riskArea: areaName, sourceName: risk.category })}
                              />
                            </Stack>
                          </Paper>
                        ))}

                        {/* Add Risk Button */}
                        <Divider label="Add Risk" labelPosition="center" />
                        {showAddRisk === category ? (
                          <Paper withBorder p="sm" bg="gray.0">
                            <Stack gap="xs">
                              <TextInput
                                placeholder="Enter risk name..."
                                value={newRiskName}
                                onChange={(e) => setNewRiskName(e.target.value)}
                                size="sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleAddRisk(category);
                                  if (e.key === 'Escape') setShowAddRisk(null);
                                }}
                              />
                              <Group gap="xs">
                                <Button size="xs" onClick={() => handleAddRisk(category)} leftSection={<IconPlus size={12} />}>
                                  Add Risk
                                </Button>
                                <Button size="xs" variant="subtle" onClick={() => setShowAddRisk(null)}>
                                  Cancel
                                </Button>
                              </Group>
                            </Stack>
                          </Paper>
                        ) : (
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => setShowAddRisk(category)}
                            fullWidth
                          >
                            Add Risk to {toTitleCase(category)}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Expanded Actions */}
            <Divider />
            <Group justify="flex-end" gap="md">
              <Button
                size="sm"
                variant="light"
                leftSection={<IconEdit size={16} />}
                onClick={() => onEditCard?.(areaName)}
              >
                Edit Sources & Scenarios
              </Button>
              <Button 
                size="sm" 
                variant="light" 
                leftSection={<IconClipboardCheck size={16} />}
                onClick={handleExpand}
              >
                Collapse View
              </Button>
            </Group>

          </Stack>
        </Collapse>

        {/* AI Refinement Modal */}
        <Modal
          opened={refineModalOpened}
          onClose={() => setRefineModalOpened(false)}
          title={<Group gap="xs"><IconSparkles size={20} color="violet" /><Text fw={600}>Refine Risk with AI</Text></Group>}
          size="lg"
        >
          <Stack gap="md">
            <Paper withBorder p="sm" bg="gray.0">
              <Text size="sm" fw={500} mb="xs">Original Risk:</Text>
              <Text size="sm">{refineRiskData?.riskName}</Text>
            </Paper>

            <Select
              label="Refinement Type"
              value={refineInstruction}
              onChange={setRefineInstruction}
              data={[
                { value: 'specific', label: 'Make more specific and focused' },
                { value: 'technical', label: 'Make more technical in wording' },
                { value: 'formal', label: 'Make more formal in tone' },
                { value: 'custom', label: 'Custom instruction' }
              ]}
            />

            {refineInstruction === 'custom' && (
              <Textarea
                label="Custom Instruction"
                placeholder="Describe how you want to refine this risk..."
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                rows={3}
              />
            )}

            <Button
              onClick={handleGenerateRefinement}
              loading={isRefining}
              leftSection={<IconSparkles size={16} />}
              disabled={refineInstruction === 'custom' && !customInstruction.trim()}
            >
              Generate Refinement
            </Button>

            {refinedSuggestion && (
              <Paper withBorder p="sm" bg="blue.0">
                <Text size="sm" fw={500} mb="xs" c="blue.8">AI Suggestion:</Text>
                <Text size="sm" c="blue.9">{refinedSuggestion}</Text>
              </Paper>
            )}

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setRefineModalOpened(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleApplyRefinement}
                disabled={!refinedSuggestion}
                color="violet"
              >
                Apply Refinement
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
}